import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { GradientTypography } from '../gradients/GradientTypography';
import { GradientIcon } from '../gradients/GradientIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import { GradientCheckbox } from '../gradients/GradientCheckbox';
import { toCamelCase, toTitleCase } from '../../util/string';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';

type Filter = {
  values: string[];
  enabled: boolean;
};

type FilterDropdownProps = {
  title: string;
  filterOptions: string[];
  handleFilterChange: (title: string, filter: Filter) => void;
  cleared: boolean;
  variant?: 'default' | 'skins';
  withSearch?: boolean;
  filterFromQuery?: Filter;
};
export function FilterDropdown({
  title,
  filterOptions,
  handleFilterChange,
  cleared,
  variant = 'default',
  withSearch = false,
  filterFromQuery,
}: FilterDropdownProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<{
    values: string[];
    enabled: boolean;
  }>({ values: [], enabled: false });
  useEffect(() => {
    if (!filterFromQuery) {
      return;
    }
    setSelected(filterFromQuery);
    setExpanded(true);
  }, [filterFromQuery]);
  const handleDelete = (input) => {
    const filtered = selected.values.filter((value) => value !== input);
    setSelected((prev) => {
      return {
        values: filtered,
        enabled: filtered.length > 0 ? prev.enabled : false,
      };
    });
  };
  const handleCheckbox = (e) => {
    const { value } = e.target;
    if (e.target.checked) {
      setSelected((prev) => {
        return {
          values: [...prev.values, value as string],
          enabled: true,
        };
      });
    } else {
      handleDelete(e.target.value);
    }
  };
  const handleClose = (e) => {
    e.stopPropagation();
    setSelected({ values: [], enabled: false });
    if (expanded) {
      setExpanded(false);
    }
  };
  const handleExpand = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    handleFilterChange(toCamelCase(title), selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  useEffect(() => {
    if (cleared) {
      setSelected({ values: [], enabled: false });
    }
  }, [cleared]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box>
        <GradientTypography
          gradientColor="primary.vertical"
          sx={{ textTransform: 'uppercase', mb: 0, ml: 2 }}
          variant="h6"
        >
          {title}
        </GradientTypography>
      </Box>
      <Accordion expanded={expanded} variant="filter">
        <AccordionSummary
          onClick={handleExpand}
          expandIcon={
            <IconButton>
              <GradientIcon
                sx={{ width: 20, height: 20 }}
                IconComponent={ExpandMoreIcon}
              />
            </IconButton>
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxHeight: selected.values.length > 0 ? 'unset' : '56px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            {selected.values.length > 0 ? (
              selected.values.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  variant="gradientOutlined"
                  sx={{ mr: 2, my: 1 }}
                  deleteIcon={
                    <CancelIcon
                      sx={{
                        width: 20,
                        height: 20,
                        mr: 2,
                        ml: -2,
                        zIndex: 1,
                        '&:hover': { cursor: 'pointer' },
                      }}
                    />
                  }
                  onDelete={() => handleDelete(item)}
                />
              ))
            ) : (
              <Typography>None selected</Typography>
            )}
          </Box>
          {selected.values.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                pl: 2,
              }}
            >
              <IconButton color="error" onClick={handleClose}>
                <GradientIcon
                  sx={{ width: 20, height: 20 }}
                  IconComponent={CloseIcon}
                  gradientColor="error.vertical"
                />
              </IconButton>
            </Box>
          ) : (
            ''
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pl: 4, pr: 0 }}>
          {withSearch && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                background: theme.palette.background.elevation[4],
                borderRadius: '65px',
                py: 2,
                px: 3,
                mb: 2,
              }}
            >
              <SearchIcon
                sx={{
                  ml: 2,
                  color: theme.palette.text.disabled,
                  width: 24,
                  height: 24,
                }}
              />
              <InputBase
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="SEARCH"
                sx={{
                  flex: 1,
                  color: theme.palette.text.secondary,
                  ml: 1,
                  height: '100%',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              />
            </Box>
          )}
          <FormControl
            sx={{
              maxHeight: 280,
              overflowY: 'auto',
              width: '100%',
            }}
          >
            {filterOptions
              .filter((option) => {
                if (searchQuery) {
                  return option
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());
                }
                return true;
              })
              .map((item) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                  key={item}
                >
                  <FormControlLabel
                    onChange={handleCheckbox}
                    control={
                      variant === 'skins' ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <GradientCheckbox
                            color="primary"
                            checked={selected.values.includes(
                              toTitleCase(item),
                            )}
                            value={toTitleCase(item)}
                          />
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              mr: 2,
                              borderRadius: '50%',
                              background:
                                theme.palette.rarityBackground[`${item}`],
                            }}
                          />
                        </Box>
                      ) : (
                        <GradientCheckbox
                          color="primary"
                          checked={selected.values.includes(toTitleCase(item))}
                          value={toTitleCase(item)}
                        />
                      )
                    }
                    label={toTitleCase(item)}
                  />
                </Box>
              ))}
          </FormControl>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
